import { v4 as uuidv4 } from 'uuid';

import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmApprove } from 'APIPackage/KnmModule/interfaces/IKnmApprove';
import { IKnmApproveDoc } from 'APIPackage/KnmModule/interfaces/IKnmApproveDoc';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { IKnmEventParams } from 'APIPackage/KnmModule/interfaces/IKnmEventParams';
import { IKnmInspector } from 'APIPackage/KnmModule/interfaces/IKnmInspector';
import { IKnmInjunction } from 'APIPackage/KnmModule/interfaces/IKnmInjunction';
import { IKnmKnoOrganizationBlockParams } from 'APIPackage/KnmModule/interfaces/IKnmKnoOrganizationBlockParams';
import { IKnmObjectParams } from 'APIPackage/KnmModule/interfaces/IKnmObjectParams';
import { IKnmParams } from 'APIPackage/KnmModule/interfaces/IKnmParams';
import { IKnmReasonParams } from 'APIPackage/KnmModule/interfaces/IKnmReasonParams';
import { IKnmRejectReasonParams } from 'APIPackage/KnmModule/interfaces/IKnmRejectReasonParams';
import { IKnmResult } from 'APIPackage/KnmModule/interfaces/IKnmResult';
import { IKnmResultInfo } from 'APIPackage/KnmModule/interfaces/IKnmResultInfo';
import { IKnmViolationLawsuit } from 'APIPackage/KnmModule/interfaces/IKnmViolationLawsuit';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { IKnmSubjectTypesERVK } from 'APIPackage/CatalogsModule/interfaces/IKnmSubjectTypesERVK';
import { IKnmErpErvkFormInspector } from 'KnmErpPackage/KnmErpKnoOrganizationModule/interfaces/IknmErpErvkFormInspector';
import { IDictionaryFederalLawSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryFederalLawSelectOption';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';

import { IKnmErpForm } from '../interfaces/IKnmErpForm';
import { convertErpObjectErvkFormToKnmObjectErvk } from './convertErpObjectErvkFormToKnmObjectErvk';
import { convertErpErvkFormInspectorToKnmInspector248 } from './convertErpErvkFormInspectorToKnmInspector248';
import { convertReasonsFormValueToKnmData } from '../../../KnmErknmPackage/MainModule/common/convertReasonsFormValueToKnmData';
import { convertErpChecklistFormToKnmChecklist } from './convertErpChecklistFormToKnmChecklist';
import { store } from '../../../CorePackage/MainModule/common/store';

const convertFormDateToParamsDate = (dateString?: string | null) => {
  if (dateString) {
    return formatDate({ date: dateString, dateFormat: SYSTEM_DATE_FORMAT_FNS });
  }

  return null;
};

const convertFormDateTimeToParamsDateTime = (dateString?: string | null) => {
  if (dateString) {
    return formatDate({
      date: dateString,
      dateFormat: DATE_FORMAT_ISO_FNS,
      parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
    });
  }

  return null;
};

export const convertKnmErpFormValuesToCreateParams = (values: IKnmErpForm, knmData?: IKnm) => {
  const {
    links,
    type,
    typeErvk,
    kind,
    kindErvk,
    testPurchaseMethod,
    testPurchaseDescription,
    subjectType,
    subjectTypeErvk,
    supervisionType,
    supervisionTypeErvk,
    startDate,
    stopDate,
    month,
    durationDays: formDurationDays,
    durationHours: formDurationHours,
    orderNumber,
    orderDate,
    headOrderNumber,
    headOrderDate,
    inspectionTarget,
    reasons,
    legalBases,
    events,
    federalLaw: federalLawErvk,
    knoOrganization,
    knoOrganizationErvk,
    collaboratingKnoName,
    collaboratingOrganizations,
    knoOrganizationFunctions: formKnoOrganizationFunctions,
    inspectors,
    inspectorsErvk,
    prosecutorOrganization,
    inspectionDecision,
    rejectReasons,
    approveOrderNumber,
    approveOrderDate,
    decisionDate,
    decisionPlace,
    signerFullName,
    signerPosition,
    licenseReason,
    licenseReasonStartDate,
    licenseReasonKnmEndDate,
    ogrn,
    inn,
    mspCode,
    mspRegistryDate,
    organizationName,
    registrationDate,
    organizationStatus,
    isSmallEnterprise,
    isSmallEnterpriseFns,
    organizationAutocomplete: isOrganizationAutocomplete,
    riskCategory,
    objects: formObjects,
    objectsErvk,
    noticeMethod,
    noticeDate,
    impossibleConductionReason,
    objectsResults,
    auditedPersonAuthority,
    resultsCancellationInfo,
    violations: formViolations,
    violationsLawsuits,
    requirements,
    checklists: formChecklists,
    documents,
    planId,
  } = values;
  const federalLaw = supervisionType?.federalLaw;
  const federalLawName = federalLaw?.name;
  const durationDays = formDurationDays ? Number(formDurationDays) || null : null;
  const durationHours = formDurationHours ? Number(formDurationHours.replace(',', '.')) || null : null;
  let approveDocuments: IKnmApprove['documents'] = null;

  if (orderNumber || orderDate) {
    const orderDocument: IKnmApproveDoc = {
      id: null,
      type: {
        id: 1,
        code: 'ORDER',
      },
      atr: orderNumber || null,
      date: orderDate ? convertFormDateToParamsDate(orderDate) : null,
      main: null,
      dateCreate: null,
    };

    if (!approveDocuments) {
      approveDocuments = [];
    }
    approveDocuments.push(orderDocument);
  }
  if (headOrderNumber || headOrderDate) {
    const orderExtensionDocument: IKnmApproveDoc = {
      id: null,
      type: {
        id: 2,
        code: 'ORDER_EXTENSION',
      },
      atr: headOrderNumber || null,
      date: headOrderDate ? convertFormDateToParamsDate(headOrderDate) : null,
      main: null,
      dateCreate: null,
    };

    if (!approveDocuments) {
      approveDocuments = [];
    }
    approveDocuments.push(orderExtensionDocument);
  }
  if (approveOrderNumber || approveOrderDate) {
    const requestDocument: IKnmApproveDoc = {
      id: null,
      type: {
        id: 3,
        code: 'REQUEST',
      },
      atr: approveOrderNumber || null,
      date: approveOrderDate ? convertFormDateToParamsDate(approveOrderDate) : null,
      main: null,
      dateCreate: null,
    };

    if (!approveDocuments) {
      approveDocuments = [];
    }
    approveDocuments.push(requestDocument);
  }

  const objects: IKnmParams['objects'] =
    formObjects?.reduce((filteredObjects: IKnmParams['objects'], formObject) => {
      let newFilteredObjects = filteredObjects ? [...filteredObjects] : null;

      if (
        !formObject.address ||
        (!formObject.addressType && formObject.isAddressHazard === undefined) ||
        !formObject.objectType
      ) {
        return newFilteredObjects;
      }

      const formObjectResult = objectsResults?.find(objectResult => objectResult.object?.id === formObject.listId);
      const actAddressType: IKnmResult['actAddressType'] = formObjectResult?.actAddressType
        ? deleteObjectProperties(formObjectResult?.actAddressType, ['label', 'value'])
        : undefined;
      const formObjectResultCancellationInfo = resultsCancellationInfo?.find(
        resultCancellationInfo => resultCancellationInfo.result?.id === formObjectResult?.object?.id,
      );
      const resultAdditionalInfoCancellationItem: IKnmResultInfo | undefined =
        formObjectResultCancellationInfo?.type && formObjectResultCancellationInfo.information
          ? {
              type: formObjectResultCancellationInfo.type,
              information: formObjectResultCancellationInfo.information,
              guid: formObjectResultCancellationInfo.guid,
            }
          : undefined;
      let resultAdditionalInfo: IKnmResult['additionalInfo'] = formObjectResult?.additionalInfo?.reduce(
        (filteredFormAdditionalInfoItems: IKnmResult['additionalInfo'], formAdditionalInfoItem) => {
          let newFilteredFormAdditionalInfoItems = filteredFormAdditionalInfoItems
            ? [...filteredFormAdditionalInfoItems]
            : null;

          if (formAdditionalInfoItem?.type && formAdditionalInfoItem?.information) {
            if (!newFilteredFormAdditionalInfoItems) {
              newFilteredFormAdditionalInfoItems = [];
            }
            newFilteredFormAdditionalInfoItems.push({
              type: deleteObjectProperties(formAdditionalInfoItem.type, ['label', 'value']),
              information: formAdditionalInfoItem.information,
              guid: formAdditionalInfoItem.guid,
            });
          }

          return newFilteredFormAdditionalInfoItems;
        },
        null,
      );

      if (resultAdditionalInfoCancellationItem) {
        if (!resultAdditionalInfo) {
          resultAdditionalInfo = [];
        }
        resultAdditionalInfo.push(resultAdditionalInfoCancellationItem);
      }
      const formObjectViolations = formViolations?.filter(
        violation => violation.result?.id === formObjectResult?.object?.id,
      );

      const result: IKnmObjectParams['result'] =
        formObjectResult && actAddressType
          ? {
              guid: formObjectResult.guid,
              actCreateDate: convertFormDateTimeToParamsDateTime(formObjectResult.actCreateDate),
              actAddress: formObjectResult.actAddress || formObjectResult.manualAddress,
              isAddressHazard: formObjectResult.isAddressHazard ?? null,
              actAddressType,
              startDate: convertFormDateTimeToParamsDateTime(formObjectResult.startDate),
              durationDays:
                formObjectResult.durationDays && !Number.isNaN(Number(formObjectResult.durationDays))
                  ? Number(formObjectResult.durationDays)
                  : null,
              durationHours: formObjectResult.durationHours
                ? Number(formObjectResult.durationHours.replace(',', '.')) || null
                : null,
              // impossibleConductionReason: , ???
              // cancellationInfo: , ???
              actWasNotRead: formObjectResult.additionalInfo?.find(
                additionalInfoItem =>
                  additionalInfoItem?.type?.code === KnmResultInfoTypeCodesEnum.ACT_WAS_NOT_READ_NOTE,
              )?.actWasNotRead,
              // actWasNotReadInfo: , ???
              // wrongNotificationData: , ???
              // violationLackInfo: , ???
              inspectors: formObjectResult.inspectors?.reduce(
                (filteredInspectors: IKnmResult['inspectors'], inspector) => {
                  let newFilteredInspectors = filteredInspectors ? [...filteredInspectors] : null;

                  if (inspector) {
                    if (!newFilteredInspectors) {
                      newFilteredInspectors = [];
                    }
                    newFilteredInspectors.push({
                      ...inspector,
                      type: deleteObjectProperties(inspector.type, ['label', 'value']),
                    });
                  }

                  return newFilteredInspectors;
                },
                null,
              ),
              delegates: formObjectResult.delegates?.reduce((filteredDelegates: IKnmResult['delegates'], delegate) => {
                let newFilteredDelegates = filteredDelegates ? [...filteredDelegates] : null;

                if (delegate) {
                  if (!newFilteredDelegates) {
                    newFilteredDelegates = [];
                  }
                  newFilteredDelegates.push({
                    ...delegate,
                    type: deleteObjectProperties(delegate.type, ['label', 'value']),
                  });
                }

                return newFilteredDelegates;
              }, null),
              additionalInfo: resultAdditionalInfo?.map(additionalInfo => {
                if (!additionalInfo.guid) {
                  return {
                    ...additionalInfo,
                    guid: uuidv4(),
                  };
                }

                return additionalInfo;
              }),
              violations: formObjectViolations?.map(formViolation => ({
                ...deleteObjectProperties(formViolation, ['result', 'isNew', 'listId']),
                type: formViolation.type && deleteObjectProperties(formViolation.type, ['label', 'value']),
                legalBases: formViolation.legalBases?.map(legalBasis => ({
                  guid: legalBasis.guid || uuidv4(),
                  ...deleteObjectProperties(legalBasis, ['id']),
                })),
                injunctions: formViolation.injunctions?.reduce(
                  (filteredInjunctions: IKnmInjunction[] | null, formInjunction) => {
                    let newFilteredInjunctions = filteredInjunctions ? [...filteredInjunctions] : null;

                    if (formInjunction) {
                      if (!newFilteredInjunctions) {
                        newFilteredInjunctions = [];
                      }
                      newFilteredInjunctions.push({
                        ...formInjunction,
                        appointmentDate: convertFormDateToParamsDate(formInjunction.appointmentDate),
                        executionDeadline: convertFormDateToParamsDate(formInjunction.executionDeadline),
                      });
                    }

                    return newFilteredInjunctions;
                  },
                  null,
                ),
                lawsuits: violationsLawsuits?.reduce(
                  (filteredLawsuits: IKnmViolationLawsuit[] | null, violationLawsuits) => {
                    let newFilteredLawsuits = filteredLawsuits ? [...filteredLawsuits] : null;

                    if (
                      violationLawsuits.violation?.id === formViolation.listId &&
                      violationLawsuits.lawsuits?.length
                    ) {
                      newFilteredLawsuits = violationLawsuits.lawsuits?.map(lawsuit => ({
                        ...lawsuit,
                        lawsuitType: deleteObjectProperties(lawsuit.lawsuitType, ['label', 'value']),
                      }));
                    }

                    return newFilteredLawsuits;
                  },
                  null,
                ),
                guid: formViolation.guid || null,
              })),
            }
          : null;

      const checklists: IKnmObjectParams['checklists'] = formChecklists
        ?.filter(formChecklist => formChecklist.object?.id === formObject.listId)
        .map(formChecklist => convertErpChecklistFormToKnmChecklist(formChecklist));

      if (!newFilteredObjects) {
        newFilteredObjects = [];
      }

      newFilteredObjects.push({
        ...deleteObjectProperties(formObject, ['listId', 'isNew']),
        address: formObject.address,
        manualAddress: formObject.manualAddress ?? null,
        notInFIAS: formObject.notInFIAS ?? null,
        fiasRegion: formObject.fiasRegion ?? null,
        isAddressHazard: formObject.isAddressHazard ?? false,
        addressType: formObject.addressType
          ? deleteObjectProperties(formObject.addressType, ['label', 'value'])
          : undefined,
        objectType: deleteObjectProperties(formObject.objectType, ['label', 'value']),
        riskCategory: formObject.riskCategory
          ? deleteObjectProperties(formObject.riskCategory, ['label', 'value'])
          : null,
        result,
        ...(checklists && { checklists }),
        supervisions:
          formObject.supervisions?.reduce(
            (filteredSupervisions: IKnmObjectParams['supervisions'] | null, formSupervision) => {
              let newFilteredSupervisions = filteredSupervisions;

              if (formSupervision) {
                if (!newFilteredSupervisions) {
                  newFilteredSupervisions = [];
                }
                newFilteredSupervisions.push({
                  supervisionType: deleteObjectProperties(formSupervision, ['label', 'value']),
                });
              }

              return newFilteredSupervisions;
            },
            null,
          ) || null,
      });

      return newFilteredObjects;
    }, null) || null;

  const isFederalLawOk = federalLawErvk || (federalLaw && typeof federalLaw.id === 'number' && federalLawName);
  const isSupervisionTypeOk = !!supervisionTypeErvk || !!supervisionType;
  const isTypeOk = !!type || !!typeErvk;

  const isErpKindValidation = isLaterThanAdminSetting(
    knmData?.created,
    getAdminFetchSettings(store.getState()),
    AdminSettingCodesEnum.ERP_KIND_VALIDATION,
  );

  const isKindOk = !!kind || !!kindErvk || !isErpKindValidation;
  const isSubjectTypeOk = !!subjectType || !!subjectTypeErvk;
  const isKnoOrganizationOk = !!knoOrganization || !!knoOrganizationErvk;

  if (
    isTypeOk &&
    isKindOk &&
    isSubjectTypeOk &&
    isSupervisionTypeOk &&
    startDate &&
    isFederalLawOk &&
    isKnoOrganizationOk &&
    prosecutorOrganization &&
    inn
  ) {
    const approveStartDate = knmData?.approve?.startDate;
    const isApproveStartDateIsMonth = knmData?.approve?.startDateIsMonth;
    const approveStopDate = knmData?.approve?.stopDate;
    const extDurationDays = knmData?.approve?.extDurationDays;
    const extDurationHours = knmData?.approve?.extDurationDays;
    const approveDateCreate = knmData?.approve?.dateCreate;
    const approveStreetGuid = knmData?.approve?.streetGuid;
    const approveHouseGuid = knmData?.approve?.houseGuid;
    const approveRoomGuid = knmData?.approve?.roomGuid;
    const approveSteadGuid = knmData?.approve?.steadGuid;
    const selectedType = type ?? typeErvk;
    const planIdFormValue = typeof planId === 'string' && !Number.isNaN(Number(planId)) ? Number(planId) : null;

    const params: IKnmParams = {
      id: typeof knmData?.id === 'number' ? knmData?.id : null,
      erpId: knmData?.erpId || null,
      planId: typeof knmData?.planId === 'number' ? knmData?.planId : planIdFormValue,
      planGuid: typeof knmData?.planGuid === 'string' ? knmData?.planGuid : null,
      guid: knmData?.guid || null,
      status: knmData?.status || null,
      year: typeof knmData?.year === 'number' ? knmData?.year : null,
      month: month?.value,
      creationDate: knmData?.creationDate || null,
      publicationDate: knmData?.publicationDate || null,
      type: selectedType ? deleteObjectProperties(selectedType, ['label', 'value']) : null,
      kind: kind ? deleteObjectProperties(kind, ['label', 'value']) : null,
      federalLaw: knmData?.federalLaw || null,
      title: knmData?.title || null,
      testPurchaseMethod: testPurchaseMethod ? deleteObjectProperties(testPurchaseMethod, ['label', 'value']) : null,
      testPurchaseDescription: testPurchaseDescription || null,
      supervisionType: supervisionType ? deleteObjectProperties(supervisionType, ['label', 'value']) : null,
      startDate: convertFormDateToParamsDate(startDate),
      stopDate: convertFormDateToParamsDate(stopDate),
      startDateIsMonth: typeof knmData?.startDateIsMonth === 'boolean' ? knmData?.startDateIsMonth : null,
      approve:
        inspectionTarget ||
        approveStartDate ||
        typeof isApproveStartDateIsMonth === 'boolean' ||
        approveStopDate ||
        durationDays ||
        durationHours ||
        extDurationDays ||
        extDurationHours ||
        decisionPlace ||
        decisionDate ||
        signerFullName ||
        signerPosition ||
        impossibleConductionReason ||
        approveDocuments ||
        approveDateCreate ||
        approveStreetGuid ||
        approveHouseGuid ||
        approveRoomGuid ||
        approveSteadGuid
          ? {
              inspectionTarget: inspectionTarget || null,
              startDate: approveStartDate || null,
              startDateIsMonth: typeof isApproveStartDateIsMonth === 'boolean' ? isApproveStartDateIsMonth : null,
              stopDate: approveStopDate || null,
              durationDays,
              durationHours,
              extDurationDays: typeof extDurationDays === 'number' ? extDurationDays : null,
              extDurationHours: typeof extDurationHours === 'number' ? extDurationHours : null,
              decisionPlace: decisionPlace || null,
              decisionDate: decisionDate ? convertFormDateToParamsDate(decisionDate) : null,
              signerFullName: signerFullName || null,
              signerPosition: signerPosition || null,
              approved: inspectionDecision?.value || false,
              impossibleConductionReason: impossibleConductionReason?.trim() || null,
              documents: approveDocuments,
              dateCreate: approveDateCreate || null,
              streetGuid: approveStreetGuid || null,
              houseGuid: approveHouseGuid || null,
              roomGuid: approveRoomGuid || null,
              steadGuid: approveSteadGuid || null,
            }
          : null,
      reasons:
        reasons?.reduce((filteredReasons: IKnmReasonParams[] | null, reason) => {
          let newFilteredReasons = filteredReasons ? [...filteredReasons] : null;

          if (reason.type) {
            if (!filteredReasons) {
              newFilteredReasons = [];
            }
            newFilteredReasons?.push({
              id: reason.id,
              type: deleteObjectProperties(reason.type, ['label', 'value']),
              text: reason.text,
              date: convertFormDateToParamsDate(reason.date),
              main: reason.main,
              approveRequired: reason.approveRequired || false,
              assignmentNumber: reason.assignmentNumber,
              assignmentDate: convertFormDateToParamsDate(reason.assignmentDate),
              requirementDetails: reason.requirementDetails,
              dateRequirement: reason.dateRequirement,
              dateBegin: reason.dateBegin,
              dateEnd: reason.dateBegin,
              links: reason.links || null,
            });
          }

          return newFilteredReasons;
        }, null) || null,
      legalBases: legalBases?.map(legalBasis => deleteObjectProperties(legalBasis, ['id'])) || null,
      events:
        events?.reduce((filteredEvents: IKnmEventParams[] | null, formEvent) => {
          let newFilteredEvents = filteredEvents ? [...filteredEvents] : null;

          if (formEvent?.name) {
            if (!newFilteredEvents) {
              newFilteredEvents = [];
            }
            newFilteredEvents.push({
              ...formEvent,
              startDate: convertFormDateToParamsDate(formEvent.startDate),
              stopDate: convertFormDateToParamsDate(formEvent.stopDate),
            });
          }

          return newFilteredEvents;
        }, null) || null,
      signStatus: knmData?.signStatus || null,
      knoOrganization: {
        organization: knoOrganization
          ? {
              id: knoOrganization.id,
              fullName: knoOrganization.fullName,
            }
          : null,
        collaboratingKnoName: collaboratingKnoName || null,
        collaboratingOrganizations:
          collaboratingOrganizations?.reduce(
            (
              filteredCollaboratingOrganizations: IKnmKnoOrganizationBlockParams['collaboratingOrganizations'],
              collaboratingOrganization,
            ) => {
              let newFilteredCollaboratingOrganizations = filteredCollaboratingOrganizations
                ? [...filteredCollaboratingOrganizations]
                : null;

              if (collaboratingOrganization?.organization) {
                if (!newFilteredCollaboratingOrganizations) {
                  newFilteredCollaboratingOrganizations = [];
                }
                newFilteredCollaboratingOrganizations?.push({
                  ...deleteObjectProperties(collaboratingOrganization, ['listId']),
                  organization: {
                    id: collaboratingOrganization.organization.id,
                    guid: collaboratingOrganization.organization.guid,
                    fullName: collaboratingOrganization.organization.fullName,
                    shortName: collaboratingOrganization.organization.shortName,
                    ogrn: collaboratingOrganization.organization.ogrn,
                    ssn: collaboratingOrganization.organization.ssn,
                    adminLevel: collaboratingOrganization.organization.adminLevel,
                    type: collaboratingOrganization.organization.type,
                    enabled: collaboratingOrganization.organization.enabled,
                    parentId: collaboratingOrganization.organization.parentId,
                    dateCreate: collaboratingOrganization.organization.dateCreate,
                  },
                });
              }

              return newFilteredCollaboratingOrganizations;
            },
            null,
          ) || null,
        functions:
          formKnoOrganizationFunctions?.reduce(
            (filteredFunctions: IKnmKnoOrganizationBlockParams['functions'], formKnoOrganizationFunction) => {
              let newFilteredFunctions = filteredFunctions ? [...filteredFunctions] : null;

              if (formKnoOrganizationFunction) {
                if (!newFilteredFunctions) {
                  newFilteredFunctions = [];
                }
                newFilteredFunctions.push(deleteObjectProperties(formKnoOrganizationFunction, ['label', 'value']));
              }

              return newFilteredFunctions;
            },
            null,
          ) || null,
      },
      inspectors:
        inspectors?.reduce((filteredInspectors: IKnmInspector[] | null, inspector, index) => {
          let newFilteredInspectors = filteredInspectors ? [...filteredInspectors] : null;

          if (inspector?.type) {
            if (!newFilteredInspectors) {
              newFilteredInspectors = [];
            }
            newFilteredInspectors.push({
              ...deleteObjectProperties(inspector, ['listId']),
              main: index === 0,
              type: deleteObjectProperties(inspector.type, ['label', 'value']),
              fullName: inspector.fullName,
            });
          }

          return newFilteredInspectors;
        }, null) || null,
      prosecutorOrganization: {
        id: null,
        code: null,
        name: prosecutorOrganization?.fullName || null,
        address: null,
        contacts: null,
        federalDistrictId: null,
        regionId: null,
        parentId: null,
        enabled: null,
        ervkUuid: prosecutorOrganization?.ervkUuid || knmData?.prosecutorOrganization?.ervkUuid,
        ervkVersionUuid: prosecutorOrganization?.ervkVersionUuid || knmData?.prosecutorOrganization?.ervkVersionUuid,
      },
      rejectReasons:
        rejectReasons?.reduce((filteredRejectReasons: IKnmRejectReasonParams[] | null, rejectReason) => {
          let newFilteredRejectReasons = filteredRejectReasons ? [...filteredRejectReasons] : null;

          if (rejectReason.type) {
            if (!newFilteredRejectReasons) {
              newFilteredRejectReasons = [];
            }
            newFilteredRejectReasons.push({
              ...deleteObjectProperties(rejectReason, ['listId']),
              type: deleteObjectProperties(rejectReason.type, ['label', 'value']),
            });
          }

          return newFilteredRejectReasons;
        }, null) || null,
      organization: {
        inn,
        ogrn,
        organizationName,
        licenseReason,
        type: subjectType ? deleteObjectProperties(subjectType, ['label', 'value']) : null,
        registrationDate: convertFormDateToParamsDate(registrationDate),
        // foundationDate: '2020-06-23',
        // foundationDateFns: '2020-06-23',
        // lastInspectionDate: '2020-06-23',
        licenseReasonStartDate: convertFormDateToParamsDate(licenseReasonStartDate),
        licenseReasonKnmEndDate: convertFormDateToParamsDate(licenseReasonKnmEndDate),
        status: organizationStatus,
        // statusFns: 'string',
        isSmallEnterprise,
        isSmallEnterpriseFns,
        autocomplete: isOrganizationAutocomplete,
        mspCode: mspCode || null,
        mspRegistryDate: mspRegistryDate || null,
      },
      riskCategory: riskCategory ? deleteObjectProperties(riskCategory, ['label', 'value']) : null,
      objects,
      noticeMethod: noticeMethod ? deleteObjectProperties(noticeMethod, ['label', 'value']) : null,
      noticeDate: convertFormDateToParamsDate(noticeDate),
      auditedPersonAuthority: auditedPersonAuthority?.map(({ authority }) => authority),
      documents:
        documents?.reduce((filteredDocuments: IKnmDocumentRequest[] | null, document) => {
          let newFilteredDocuments = filteredDocuments ? [...filteredDocuments] : null;

          if (document && document.type) {
            const documentAttachments = document.attachments?.reduce(
              (filteredDocumentAttachments: IKnmDocumentRequest['attachments'] | null, documentAttachment) => {
                let newFilteredDocumentAttachments = filteredDocumentAttachments;

                if (
                  documentAttachment.guid &&
                  documentAttachment.dateCreate &&
                  documentAttachment.filePath &&
                  documentAttachment.mimeType &&
                  documentAttachment.bucketName
                ) {
                  if (!newFilteredDocumentAttachments) {
                    newFilteredDocumentAttachments = [];
                  }
                  newFilteredDocumentAttachments.push({
                    ...deleteObjectProperties(documentAttachment, ['isNew', 'listId', 'file']),
                    guid: documentAttachment.guid,
                    dateCreate: documentAttachment.dateCreate,
                    filePath: documentAttachment.filePath,
                    mimeType: documentAttachment.mimeType,
                    bucketName: documentAttachment.bucketName,
                    correlationToken: documentAttachment.correlationToken || null,
                    hash: documentAttachment.hash || null,
                    signed: typeof documentAttachment.signed === 'boolean' ? documentAttachment.signed : null,
                    signatureInfo: documentAttachment.signatureInfo || null,
                  });
                }

                return newFilteredDocumentAttachments;
              },
              null,
            );

            if (!newFilteredDocuments) {
              newFilteredDocuments = [];
            }
            newFilteredDocuments.push({
              ...deleteObjectProperties(document, ['listId']),
              type: deleteObjectProperties(document.type, ['label', 'value']),
              attachments: documentAttachments,
            });
          }

          return newFilteredDocuments;
        }, null) || null,
      links:
        links?.map(formLink => ({
          type: formLink.type,
          guid: formLink.guid || null,
          erpId: formLink.erpId,
        })) || null,
      district: {
        id: prosecutorOrganization.districtId,
        name: prosecutorOrganization.districtName,
        center: null,
        okatoTer: null,
        okatoKod1: null,
        okatoRazdel: null,
        federalRegionCode: null,
        federalDistrict: null,
      },
      requirements:
        requirements?.map(formRequirement => ({
          name: formRequirement.name,
          template: formRequirement.template,
          documents: formRequirement.documents || null,
          knoOrganization: formRequirement.knoOrganization,
          supervisionType: formRequirement.supervisionType,
          guid: formRequirement.guid,
        })) || null,
      deleted: typeof knmData?.deleted === 'boolean' ? knmData?.deleted : null,
      publishedStatus: knmData?.publishedStatus || null,
      manualPublish: typeof knmData?.manualPublish === 'boolean' ? knmData?.manualPublish : null,
      published: typeof knmData?.published === 'boolean' ? knmData?.published : null,
      correlationToken: knmData?.correlationToken || null,
      createdBy: knmData?.createdBy || null,
      updatedBy: knmData?.updatedBy || null,
      created: knmData?.created || null,
      updated: knmData?.updated || null,
      note: knmData?.note || null,
      hash: knmData?.hash || null,
      signed: typeof knmData?.signed === 'boolean' ? knmData?.signed : null,
      draft: typeof knmData?.draft === 'boolean' ? knmData?.draft : null,
      draftSavedAt: knmData?.draftSavedAt || null,
      signatureInfo: knmData?.signatureInfo || null,
      dateCreate: knmData?.dateCreate || null,
      classificationDateCreate: knmData?.classificationDateCreate || null,
      publishDateCreate: knmData?.publishDateCreate || null,
      statusDateCreate: knmData?.statusDateCreate || null,
      subjectNotifications: null,
      reasonsErvk: convertReasonsFormValueToKnmData(values?.reasonsErvk),
      objectsErvk:
        objectsErvk?.map(obj =>
          convertErpObjectErvkFormToKnmObjectErvk(
            obj,
            objectsResults,
            formViolations,
            resultsCancellationInfo,
            violationsLawsuits,
            formChecklists,
          ),
        ) || null,
      knmErknm: getKnmErknm(
        typeErvk,
        knoOrganizationErvk,
        supervisionTypeErvk,
        kindErvk,
        subjectTypeErvk,
        inspectorsErvk,
        federalLawErvk,
      ),
      pmErknm: null,
      version: KnmVersionsEnum.ERP,
      firstExtractDateTime: convertFormDateTimeToParamsDateTime(knmData?.firstExtractDateTime) || null,
    };

    return params;
  }

  return null;
};

function getKnmErknm(
  typeErvk?: IKnmTypeSelectOption,
  knoOrganizationErvk?: IDictionaryKnoOrganizationSelectOption,
  supervisionTypeErvk?: IDictionarySupervisionTypeSelectOption,
  kindErvk?: IDictionarySelectOption<IKnmKind>,
  subjectTypeErvk?: IDictionarySelectOption<IKnmSubjectTypesERVK>,
  inspectorsErvk?: IKnmErpErvkFormInspector[],
  federalLawErvk?: IDictionaryFederalLawSelectOption, //+
) {
  if (!typeErvk) return null;
  const knmErknm: IKnmParams['knmErknm'] = {
    federalLawErvk: federalLawErvk?.value ?? null,
    subjectTypeErvk: subjectTypeErvk?.value ?? null,
    accessToken: null,
    knoOrganization: knoOrganizationErvk?.value ?? null,
    supervisionTypeErknm: null,
    decision: null,
    durationDays: null,
    durationHours: null,
    directDurationDays: null,
    kindControl: supervisionTypeErvk?.value ?? null,
    abrogation: null,
    rescheduling: null,
    kindKnm: kindErvk?.value ?? null,
    reasonDocuments: null,
    dataContent: null,
    prosecutorDocuments: null,
    approve: null,
    organizations: null,
    judgments: null,
    reasons: null,
    objects: null,
    inspectors: inspectorsErvk?.map(convertErpErvkFormInspectorToKnmInspector248) ?? null,
    experts: null,
    collaboratingOrganizationChangeDate: null,
    collaboratingOrganizations: null,
    events: null,
    requirements: null,
    requirementsNew: null,
    approveDocs: null,
    docTarget: null,
    isChecklistsUsed: null,
    checklists: null,
    places: null,
    organizationDocuments: null,
    serviceDocument: null,
    isRemote: null,
    videoCallInfo: null,
    interactionCategory: null,
    noteRemote: null,
    withVideo: null,
    isPresence: null,
    isPresenceDelegateFullNames: null,
    isSelection: null,
    returnSelection: null,
    selection: null,
    attachmentsWeb: null,
    documentRequestDate: null,
    documentResponseDate: null,
    manuallyRequirements: null,
    manuallyCheckList: null,
    isHasCollaboratingOrganization: null,
    changePlanReason: null,
    changePlanChanges: null,
    appealStatus: null,
    isAppealed: null,
    oldKnmNumber: null,
    oldPlanNumber: null,
    webNotificationInfos: null,
    accessPrivilege: null,
    untimelyBasic: null,
    notNotifySubject: null,
    ervkCorrectionRequired: null,
    initialStopDate: null,
    completedRO: null,
    extractPerform: null,
    extractVersion: null,
    firstExtractDateTime: null,
    selectionReactQuillEditor: null,
    agreements: null,
    agreementOnApproval: null,
    subjectsTypeChangeOptionAvailable: null,
    isApprovedOnPaper: null,
    approvedOnPaperDocuments: null,
    repeatPassports: null,
  };

  return knmErknm;
}
